import { View } from "@novorender/api";
import { vec3 } from "gl-matrix";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import type { AppDispatch, RootState } from "app/store";
import { CanvasHudLineLabel } from "components/canvasHud/lineLabel";
import { ensureLabelIsOnScreen, LineLabel, updateLineLabel2d } from "components/canvasHud/lineLabelMath";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    axisColors,
    type AxisPartName,
    isAxisPartName,
    measureLineLabelBgColor,
    measureLineLabelTextColor,
} from "features/engine2D";
import { undoRedoActions } from "features/undoRedo";
import { useUom } from "features/uom/hooks/useUom";
import { useCanRemoveMeasurements } from "hooks/useCanRemoveMeasurements";

import { measureActions, selectObjectMeasure, selectObjectMeasureResult } from "./measureSlice";
import { useObjectMeasureAxisDrawProduct } from "./objectMeasureDraw";
import { formatObjectMeasureDistance, readsAsZero } from "./objectMeasureFormat";
import { separateLabels } from "./objectMeasureLabelLayout";

type ObjectMeasureLabel = LineLabel & { text: string };

const LINE_LABEL_ID = "om-line-label";
const axisLabelId = (axis: AxisPartName) => `om-${axis}-label`;

/**
 * The distance labels for the object-to-object measurement: the total on the connecting line, and
 * one per axis on its dashed line. Rendered as HTML rather than canvas text, in the same pill style
 * point-to-point uses, and positioned every frame through the shared `update` handle.
 */
export const ObjectMeasureHtmlInteractions = forwardRef(function ObjectMeasureHtmlInteractions(_props, ref) {
    const {
        state: { view, size },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const result = useAppSelector(selectObjectMeasureResult);
    const canRemove = useCanRemoveMeasurements();
    const uom = useUom();
    const axisDrawProduct = useObjectMeasureAxisDrawProduct(view, result);
    const containerRef = useRef<HTMLDivElement>(null);

    const { lineLabel, axisLabels } = useMemo(() => {
        const from = result?.measureInfoA?.point;
        const to = result?.measureInfoB?.point;
        if (!result || !from || !to || result.distance === undefined) {
            return { lineLabel: undefined, axisLabels: [] as [AxisPartName, ObjectMeasureLabel][] };
        }

        const format = (distance: number) => formatObjectMeasureDistance(uom, distance);

        const distances: Record<AxisPartName, number> = {
            "x-axis": result.distanceX,
            "y-axis": result.distanceY,
            "z-axis": result.distanceZ,
        };

        const axes: [AxisPartName, ObjectMeasureLabel][] = [];
        for (const part of axisDrawProduct?.objects[0]?.parts ?? []) {
            const [start, end] = part.vertices3D;
            // An axis the two points barely differ along has nothing to show but a zero.
            if (!isAxisPartName(part.name) || !start || !end || readsAsZero(uom, distances[part.name])) {
                continue;
            }
            axes.push([
                part.name,
                {
                    text: format(distances[part.name]),
                    position: vec3.lerp(vec3.create(), start, end, 0.5),
                    start,
                    end,
                    dist2d: 0,
                },
            ]);
        }

        return {
            lineLabel: {
                text: format(result.distance),
                position: vec3.lerp(vec3.create(), from, to, 0.5),
                start: from,
                end: to,
                dist2d: 0,
            } as ObjectMeasureLabel,
            axisLabels: axes,
        };
    }, [result, axisDrawProduct, uom]);

    const position = useMemo(
        () => () => {
            if (!view || !containerRef.current) {
                return;
            }
            updateLabels2d(view, size.width, size.height, lineLabel, axisLabels);
            updateElementPositions(containerRef.current, lineLabel, axisLabels);
        },
        [view, size, lineLabel, axisLabels],
    );

    useImperativeHandle(ref, () => ({ update: position }), [position]);

    // The labels are mounted with no position of their own, so place them once up front rather than
    // waiting for the next frame to move them in from off screen.
    useEffect(position, [position]);

    return (
        <div ref={containerRef}>
            {lineLabel && (
                <CanvasHudLineLabel
                    id={LINE_LABEL_ID}
                    bgColor={measureLineLabelBgColor}
                    textColor={measureLineLabelTextColor}
                    onRemove={canRemove ? () => removeMeasurement(dispatch, store.getState()) : undefined}
                >
                    {lineLabel.text}
                </CanvasHudLineLabel>
            )}
            {axisLabels.map(([axis, label]) => (
                <CanvasHudLineLabel
                    key={axis}
                    id={axisLabelId(axis)}
                    bgColor={axisColors[axis].labelBg}
                    textColor={axisColors[axis].labelText}
                >
                    {axis[0].toUpperCase()}: {label.text}
                </CanvasHudLineLabel>
            ))}
        </div>
    );
});

/** Drops the measurement and its picks, as a single undoable action. */
function removeMeasurement(dispatch: AppDispatch, state: RootState) {
    const removed = selectObjectMeasure(state);
    dispatch(measureActions.clearObjectMeasure());
    dispatch(
        undoRedoActions.push({
            description: "Remove object to object measurement",
            undoCallback: () => dispatch(measureActions.setObjectMeasure(removed)),
            redoCallback: () => dispatch(measureActions.clearObjectMeasure()),
        }),
    );
}

function updateLabels2d(
    view: View,
    width: number,
    height: number,
    lineLabel: ObjectMeasureLabel | undefined,
    axisLabels: [AxisPartName, ObjectMeasureLabel][],
) {
    const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false, clipFarPlane: true });

    for (const label of [lineLabel, ...axisLabels.map(([, label]) => label)]) {
        if (!label) {
            continue;
        }
        // A label is kept even when the line is too short to fit it, covering the line instead -
        // otherwise a short measurement would show no value at all.
        updateLineLabel2d(label, view, toScreenSpace, { hideWhenShort: false });
        ensureLabelIsOnScreen(label, width, height, 70);
    }
}

function updateElementPositions(
    container: HTMLDivElement,
    lineLabel: ObjectMeasureLabel | undefined,
    axisLabels: [AxisPartName, ObjectMeasureLabel][],
) {
    const labels = [
        { id: LINE_LABEL_ID, label: lineLabel },
        ...axisLabels.map(([axis, label]) => ({ id: axisLabelId(axis), label })),
    ].map(({ id, label }) => ({ element: container.querySelector(`#${id}`) as HTMLElement | null, label }));

    separateLabels(labels);

    for (const { element, label } of labels) {
        if (!element) {
            continue;
        }
        element.style.left = `${label?.position2d?.[0] ?? -100}px`;
        element.style.top = `${label?.position2d?.[1] ?? -100}px`;
    }
}
